import { Box, Container, Paper, Typography } from "@material-ui/core";
import Snackbar from "@material-ui/core/Snackbar";
import { makeStyles } from "@material-ui/core/styles";
import MuiAlert from "@material-ui/lab/Alert";
import jseu from "js-encoding-utils";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import HttpClient from "../api/HttpClient";
import EmployeeSignButton from "../component/EmployeeSignButton";
import ModalContainer from "../component/ModalContainer";
import NavBar from "../component/NavBar";
import StaffFormFields from "../component/StaffFormFields";
import { useLoading } from "../constant/LoadingContext";
import "../styles/styles.css";
import { transformFormErrors } from "../utils";
import CompanyHeader from "./CompanyHeader";
import { useGlobalData } from "../constant/GlobalDataContext";

interface FormInputs {
  english_name: string;
  chinese_name: string;
  english_name_alias: string;
  hkid: string;
  hkid_last_digit: string;
  date_of_birth: string;
  nationality: string;
  gender: "M" | "F" | undefined;
  marriage_status: "single" | "married" | undefined;
  chinese_address: string;
  english_address: string;
  phone_number: string;
  house_phone_number: string;
  email: string;
  bank_account_holder: string;
  bank_name: string;
  bank_account_number: string;
  emergency_contact_name: string;
  emergency_contact_relationship: string;
  emergency_contact_phone_number: string;
  emergency_contact_house_number: string;
  id: string;
}

const StaffPersonalInfoForm: React.FC = () => {
  const classes = useStyles();
  const { setLoading, loading } = useLoading();
  const navigate = useNavigate();
  const { globalData, setGlobalData } = useGlobalData();
  const location = useLocation();
  const targetInfo = location.state?.targetInfo || globalData?.targetInfo || {}; // Convert queryBrand to string if it's Uint8Array
  const brandString = location.state?.brand || globalData?.brand || "PM";
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [openModal, setOpenModal] = useState(false);
  // New states for signature
  const [signature, setSignature] = useState("");
  const [submitFormError, setSubmitFormError] = useState<Partial<FormInputs>>(
    {} as Partial<FormInputs>
  );


  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    if (targetInfo?.id) {
      HttpClient().GetStaffSignStatusApi(
        { id: targetInfo?.id },
        (data) => {
          if (data?.isPersonalFormSigned && data?.isAllOtherDocumentsSigned) {
            setSnackbar({
              open: true,
              message: "所有文件已簽署",
              severity: "success",
            });
            navigate("/p/main/");
          } else if (
            data?.isPersonalFormSigned &&
            !data?.isAllOtherDocumentsSigned
          ) {
            setSnackbar({
              open: true,
              message: "個人資料已簽署",
              severity: "error",
            });
            setGlobalData({
              searchValue: targetInfo?.id,
            });
            navigate("/p/main/documentLists", {
              state: { searchValue: targetInfo?.id },
            });
            
          }
        },
        (error) => {
          setSnackbar({
            open: true,
            message: "獲取簽署狀態失敗",
            severity: "error",
          });
        }
      );
    }
  }, []);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<FormInputs>({
    defaultValues: {
      english_name: targetInfo?.english_name || "",
      chinese_name: targetInfo?.chinese_name || "",
      english_name_alias: targetInfo?.english_name_alias || "",
      hkid: targetInfo?.hkid || "",
      hkid_last_digit: targetInfo?.hkid_last_digit || "",
      date_of_birth: targetInfo?.date_of_birth || "",
      nationality: targetInfo?.nationality || "",
      gender: targetInfo?.gender || undefined,
      marriage_status: targetInfo?.marriage_status || undefined,
      chinese_address: targetInfo?.chinese_address || "",
      english_address: targetInfo?.english_address || "",
      phone_number: targetInfo?.phone_number ? String(targetInfo.phone_number) : "",
      house_phone_number: targetInfo?.house_phone_number || "",
      email: targetInfo?.email || "",
      bank_account_holder: targetInfo?.bank_account_holder || "",
      bank_name: targetInfo?.bank_name || "",
      bank_account_number: targetInfo?.bank_account_number || "",
      emergency_contact_name: targetInfo?.emergency_contact_name || "",
      emergency_contact_relationship:
        targetInfo?.emergency_contact_relationship || "",
      emergency_contact_phone_number: targetInfo?.emergency_contact_phone_number
        ? String(targetInfo.emergency_contact_phone_number)
        : "",
      emergency_contact_house_number:
        targetInfo?.emergency_contact_house_number || "",
      id: targetInfo?.id || "",
    },
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: (values) => {
      const errors: Record<string, { type: string; message: string }> = {};

      // 驗證出生日期
      if (moment(values.date_of_birth).isSame(moment(), "year")) {
        errors.date_of_birth = {
          type: "manual",
          message: "出生日期不可為今年",
        };
      }

      // 驗證電郵格式
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
      if (!emailRegex.test(values.email)) {
        errors.email = {
          type: "pattern",
          message: "請輸入正確的電郵地址",
        };
      }

      return {
        values,
        errors: Object.keys(errors).length > 0 ? errors : {},
      };
    },
  });

  // Callback to handle signature capture from ModalContainer
  const handleSignature = (pngDataUrl: string, svg: string) => {
    setSignature(pngDataUrl);
    setOpenModal(false);
  };

  // 實際的表單提交邏輯
  const onSubmit = async (data: FormInputs) => {
    if (loading) {
      return;
    }
    setLoading(true);
    const param = {
      english_name: data.english_name,
      english_name_alias: data.english_name_alias,
      chinese_name: data.chinese_name,
      date_of_birth: data.date_of_birth,
      hkid: data.hkid,
      hkid_last_digit: data.hkid_last_digit,
      ...(data.nationality !== "" ? { nationality: data.nationality } : {}),
      gender: data.gender,
      marriage_status: data.marriage_status,
      chinese_address: data.chinese_address,
      english_address: data.english_address,
      phone_number: Number(data.phone_number),
      whatsapp_number: null,
      house_phone_number: data.house_phone_number
        ? Number(data.house_phone_number)
        : null,
      email: data.email,
      bank_account_holder: data.bank_account_holder,
      bank_name: data.bank_name,
      bank_account_number: data.bank_account_number,
      emergency_contact_name: data.emergency_contact_name,
      emergency_contact_relationship: data.emergency_contact_relationship,
      emergency_contact_phone_number: Number(
        data.emergency_contact_phone_number
      ),
      emergency_contact_house_number: data.emergency_contact_house_number
        ? Number(data.emergency_contact_house_number)
        : null,
      staff_code: null,
      area: location.state?.area || "hkOffice",
      brand: brandString || "PM",
      isSync: false,
      onlyUpdateOnce: null,
      ...(signature
        ? {
            staff_signature: JSON.stringify(signature),
            staff_signature_date: moment().format("YYYY-MM-DD"),
            signatureImage: signature,
          }
        : {}),
    };

    try {
      let response;
      // Check if we're updating or creating
      if (data.id) {
        response = await HttpClient().UpdateStaffFormApi({
          id: data.id,
          param,
          onlyUpdateOnce: true,
        });
      } else {
        response = await HttpClient().CreateStaffFormApi({
          param,
        });
      }

      if (response?.success) {
        setSnackbar({
          open: true,
          message: "提交成功",
          severity: "success",
        });
        setGlobalData({
          searchValue: data.id || response?.data?.id,
        });
        navigate("/p/main/documentLists", {
          state: { searchValue: data.id || response?.data?.id },
        });
      } else {
        let errorMessages: string[] = [];
        if (response?.message && typeof response?.message === "string") {
          errorMessages.push(response.message);
        }
        // 處理錯誤對象
        if (response.error && typeof response.error === "object") {
          Object.values(response.error).forEach((msgs: any) => {
            if (Array.isArray(msgs)) {
              msgs.forEach((msg: string) => errorMessages.push(msg));
            } else if (typeof msgs === "string") {
              errorMessages.push(msgs);
            }
          });
        }

        // 處理直接的字段錯誤
        const responseKeys = Object.keys(response);
        const hasDirectFieldErrors = responseKeys.some(
          (key) =>
            Array.isArray(response[key]) || typeof response[key] === "string"
        );

        if (!response.success && !response.message && hasDirectFieldErrors) {
          Object.values(response).forEach((msgs: any) => {
            if (Array.isArray(msgs)) {
              msgs.forEach((msg: string) => errorMessages.push(msg));
            } else if (typeof msgs === "string") {
              errorMessages.push(msgs);
            }
          });
        }

        const combinedErrorMsg = errorMessages.join("; ");
        setSnackbar({
          open: true,
          message: `提交失敗: ${combinedErrorMsg || "請檢查資料是否正確"}`,
          severity: "error",
        });
      }
    } catch (error: any) {
      setSubmitFormError(error?.response?.data?.error);
      setSnackbar({
        open: true,
        message: `提交失敗: ${
          error?.response?.data?.message || "請檢查資料是否正確"
        }`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <NavBar isBack={true} step={1} isNeedPW={true} />
      <Box className={classes.container}>
        <Container>
          <Paper className={classes.paper}>
            <CompanyHeader brand={brandString || "PM"} />
            <Box className={classes.headerContainer}>
              <Typography variant="h6" className={classes.title}>
                員工個人資料
              </Typography>
              <Box className={classes.subtitle}>* 為必填項目</Box>
            </Box>

            <form>
              <StaffFormFields
                register={register}
                getValues={getValues}
                control={control}
                errors={{ ...errors, ...transformFormErrors(submitFormError) }}
                signature={signature}
                setOpenModal={setOpenModal}
                handleSubmit={handleSubmit}
                onSubmit={onSubmit}
                onError={() => {
                  setSnackbar({
                    open: true,
                    message: "資料有誤，請檢查",
                    severity: "error",
                  });
                }}
              />
              {!signature && (
                <Box display="flex" justifyContent="center" mt={4}>
                  <EmployeeSignButton
                    onClick={() => setOpenModal(true)}
                    signature={signature}
                  />
                </Box>
              )}
            </form>
          </Paper>
        </Container>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
        <ModalContainer
          open={openModal}
          setOpen={setOpenModal}
          type="sign"
          SignTrigger={handleSignature}
        />
      </Box>
    </>
  );
};

export default StaffPersonalInfoForm;

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: "#F5F5F5",
    padding: theme.spacing(3),
    minHeight: "100vh",
    [`@media (max-width: 600px)`]: {
      padding: "24px 0",
    },
  },
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    [`@media (max-width: 960px)`]: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      margin: "20px 0",
    },
  },
  paper: {
    padding: "40px 5%",
    maxWidth: 1280,
    margin: "0 auto",
    position: "relative",
    borderRadius: "32px",
    boxShadow: "0px 0px 15px 0px #0000001A",
    [`@media (max-width: 960px)`]: {
      padding: "20px 5% 40px 5%",
    },
    [`@media (max-width: 600px)`]: {
      borderRadius: "16px",
    },
  },
  title: {
    fontSize: "20px",
    letterSpacing: "4%",
    marginBottom: theme.spacing(3),
    color: "#454C96",
    fontWeight: 500,
    marginTop: "60px",
    [`@media (max-width: 960px)`]: {
      marginTop: 0,
      marginBottom: 0,
    },
  },
  subtitle: {
    color: "#84909F",
    fontSize: "16px",
    fontWeight: 400,
    marginBottom: "30px",
    [`@media (max-width: 960px)`]: {
      marginBottom: 0,
    },
  },
  button: {},
  media: {},
  blueTitle: {
    color: "#454C96",
    fontSize: "20px",
    fontWeight: 600,
    textAlign: "center",
    marginBottom: theme.spacing(2),
  },
  portion: {
    display: "flex",
    justifyContent: "space-around",
    marginTop: theme.spacing(2),
  },
  signatureDate: {
    textAlign: "center",
  },
  line: {
    width: "100%",
    border: "1px solid #ccc",
    margin: theme.spacing(1, 0),
  },
  content: {
    fontSize: "16px",
  },
  contentDate: {
    fontSize: "16px",
  },
  leftButton: {
    marginTop: theme.spacing(2),
  },
  hrConfirmBtn: {
    marginTop: theme.spacing(4),
    textAlign: "center",
  },
}));
